/**
 * Client for the history-aware lobby category-suggestions read (issue #37, FR-10.4).
 *
 * `GET /api/games/:id/suggestions` is a control-plane read served off the WS hot path
 * (cf. FR-10.3): it returns categories the currently-seated group has NOT recently
 * played, to prime the dynamic-game lobby input. It is a best-effort UX aid — a
 * failure/slow/empty response degrades to plain manual entry (D37.8), so this helper
 * never throws: any error resolves to an empty list. Unauthenticated (a lobby read
 * keyed on gameId, like the game summary), so no guest token is sent.
 *
 * `exclude` (issue #61) carries the categories already surfaced to the player this
 * session (plus what they've typed) so a "give me different ones" refresh returns
 * genuinely new categories. Sent as repeated `?exclude=` params and capped at 100 to
 * bound the query string (the server also clamps defensively).
 */

import type { CategorySuggestions } from '@jeopardy/shared';
import { SERVER_HTTP_URL } from '../config';

/** Upper bound on excluded categories sent per request (query-string guard, D61.4). */
export const MAX_SUGGESTION_EXCLUSIONS = 100;

/**
 * Client-side fetch timeout (#67). The server caps its own work at ~8s, but a hung
 * connection (a Bedrock call that didn't honor cancel) would otherwise leave the fetch
 * pending forever and the lobby stuck showing the loading state — the empty-state
 * fallback only renders once this resolves. A bounded abort guarantees it resolves, so
 * the deterministic empty state always renders. Sits above the server's 8s budget.
 */
export const SUGGESTIONS_FETCH_TIMEOUT_MS = 12_000;

/** Fetch category suggestions for a game's lobby. Returns [] on any error. */
export async function fetchCategorySuggestions(
  gameId: string,
  exclude: string[] = [],
): Promise<string[]> {
  // Bounded abort (#67) via AbortController + setTimeout (portable across browsers and
  // the jsdom test env, unlike AbortSignal.timeout). Guarantees the fetch settles so the
  // lobby's deterministic empty-state renders even if the server/Bedrock hangs.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), SUGGESTIONS_FETCH_TIMEOUT_MS);
  try {
    const params = new URLSearchParams();
    for (const c of exclude.slice(0, MAX_SUGGESTION_EXCLUSIONS)) {
      params.append('exclude', c);
    }
    const qs = params.toString();
    const res = await fetch(
      `${SERVER_HTTP_URL}/api/games/${encodeURIComponent(gameId)}/suggestions` +
        (qs ? `?${qs}` : ''),
      { signal: controller.signal },
    );
    if (!res.ok) return [];
    const body = (await res.json()) as CategorySuggestions;
    return Array.isArray(body.suggestions) ? body.suggestions : [];
  } catch {
    return [];
  } finally {
    clearTimeout(timer);
  }
}
